import React, { useState, useEffect } from 'react';

const styles = {
    modalOverlay: {
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000,
    },
    modal: {
        background: "#fff", padding: "20px", borderRadius: "8px", width: "500px",
        maxHeight: '90vh', overflowY: 'auto'
    },
    modalActions: { display: "flex", justifyContent: "flex-end", marginTop: "15px", gap: "10px", },
    save: { background: "#007bff", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, },
    cancel: { background: "#6c757d", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, },
    input: { padding: "10px", borderRadius: "4px", border: "1px solid #ccc", width: '95%'},
    monthGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' },
    monthLabel: { cursor: 'pointer', display: 'flex', alignItems: 'center', width: '30%', backgroundColor: '#f9f9f9', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' },
    activeMonth: { backgroundColor: '#e0f7fa', border: '1px solid #00bcd4' },
    buttonAction: { padding: '8px 12px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
};

export default function RecurrenceMonthsModal({
    show,
    initialData, 
    onClose,
    onSave, // Fonction finale de sauvegarde vers l'API
}) {
    const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
    // ❌ SUPPRESSION DE selectedMonths: [const [selectedMonths, setSelectedMonths] = useState([]);]
    const [checkedMonths, setCheckedMonths] = useState(Array(12).fill(false));

    const allMonths = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
    ];

    // Calculer les mois actuellement sélectionnés à partir de checkedMonths
    const currentSelectedCount = checkedMonths.filter(Boolean).length;
    
    // L'ID du mois (1-12) est utilisé pour la logique.
    useEffect(() => {
        if (!initialData || !show) return;

        if (initialData.startDate) {
            setStartDate(initialData.startDate);
        }

        const rawMonths = initialData.recurrenceMonths || initialData.initial_months_cycle;
    
        let monthsArray = [];
        if (Array.isArray(rawMonths)) {
            monthsArray = rawMonths;
        } else if (typeof rawMonths === 'string') {
            try {
                monthsArray = JSON.parse(rawMonths);
            } catch (e) {
                monthsArray = [];
            }
        }

        if (monthsArray.length > 0) {
            const initialCheckedState = Array(12).fill(false);
            monthsArray.forEach(monthId => {
                const mId = Number(monthId);
                if (mId >= 1 && mId <= 12) {
                    initialCheckedState[mId - 1] = true;
                }
            });
            setCheckedMonths(initialCheckedState);
        } else {
            setCheckedMonths(Array(12).fill(false));
        }
    }, [initialData, show]); 

    if (!show) return null;

    const handleMonthToggle = (monthNumber) => {
        // Met à jour le tableau de booleans (checkedMonths)
        setCheckedMonths(prev => {
            const newState = [...prev];
            // Le monthNumber est 1-basé, donc l'index est monthNumber - 1
            newState[monthNumber - 1] = !newState[monthNumber - 1]; 
            return newState;
        });
    };

    const handleSelectAll = () => {
        // 💡 UTILISE checkedMonths : Si tous sont sélectionnés, on désélectionne tout, sinon on sélectionne tout.
        const shouldDeselectAll = currentSelectedCount === 12;
        setCheckedMonths(Array(12).fill(!shouldDeselectAll));
    };

    const handleFinalSave = () => {
        // ✅ ÉTAPE 2 VÉRIFIÉE : Calculer les mois sélectionnés
        const selectedMonthsIds = checkedMonths.map((isChecked, index) => isChecked ? index + 1 : null).filter(Boolean);
        
        // Validation minimale
        if (!startDate || selectedMonthsIds.length === 0) {
            alert("Veuillez choisir une date de début et au moins un mois.");
            return;
        }
        
        // Préparation des données finales
        const finalData = {
            ...initialData, 
            startDate: startDate, 
            recurrenceMonths: selectedMonthsIds, // Renvoie l'array [1, 5, 8, ...]
        };

        onSave(finalData); // Exécute la fonction de sauvegarde dans le composant parent
    };

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h3>Planification de la récurrence</h3>
                
                {/* Date de Début d'Application */}
                <div style={{ marginBottom: '15px' }}>
                    <label>Date de début d'application :</label>
                    <input 
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={styles.input}
                        required
                    />
                </div>

                {/* SÉLECTION DES MOIS */}
                <h4>Répéter chaque année les mois suivants :</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <button onClick={handleSelectAll} style={{ ...styles.buttonAction, backgroundColor: currentSelectedCount === 12 ? '#ffc107' : '#28a745', color: 'white' }}>
                        {currentSelectedCount === 12 ? 'Tout Désélectionner' : 'Tout Sélectionner'}
                    </button>
                    <span>{currentSelectedCount} / 12 mois sélectionnés</span>
                </div>
                
                <div style={styles.monthGrid}>
                    {allMonths.map((monthName, index) => {
                        const monthNumber = index + 1;
                        // 💡 UTILISE checkedMonths pour le rendu
                        const isSelected = checkedMonths[index]; 

                        return (
                            <label 
                                key={monthNumber} 
                                style={{ 
                                    ...styles.monthLabel, 
                                    ...(isSelected ? styles.activeMonth : {}) // 💡 CORRECTION VISUELLE
                                }}
                            >
                                <input 
                                    type="checkbox"
                                    // 💡 CORRECTION : L'état est lu directement de checkedMonths
                                    checked={isSelected} 
                                    // Pas besoin de onChange si le parent gère le clic sur la balise <label>
                                    readOnly={true} 
                                    onChange={() => handleMonthToggle(monthNumber)}
                                    style={{ marginRight: '5px' }}
                                />
                                {monthName}
                            </label>
                        );
                    })}
                </div>

                <div style={styles.modalActions}>
                    <button type="button" onClick={onClose} style={styles.cancel}>Annuler</button>
                    <button type="button" onClick={handleFinalSave} style={styles.save}>
                        Enregistrer la récurrence
                    </button>
                </div>
            </div>
        </div>
    );
}